import * as THREE from 'three';

const STAR_COUNT = 8000;
// Stars sit on a hollow shell that is re-centered on the camera every
// frame, so the radius only needs to fit comfortably inside the camera
// far plane (20000) — anything beyond that gets clipped, anything inside
// the near plane disappears mid-flight. Picked a band that gives obvious
// parallax-feel without ever crossing in front of the player ship.
const STAR_FIELD_RADIUS = 9000;
const STAR_FIELD_RADIUS_MIN = 4000;
const AMBIENT_COLOR = 0x334455;
const DIR_LIGHT_COLOR = 0xccddee;
const FOG_COLOR = 0x000005;
const FOG_NEAR = 200;
// Fog reach is tuned so a player at the center of a sector does NOT see
// everything across it — two planets rarely end up visible in one view.
// Tune together with `SECTOR_SIZE` in shared constants.
const FOG_FAR = 2600;

export class GameScene {
  private scene: THREE.Scene;
  private entities = new Map<string, THREE.Object3D>();
  private starField!: THREE.Points;

  constructor() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(FOG_COLOR);
    this.scene.fog = new THREE.Fog(FOG_COLOR, FOG_NEAR, FOG_FAR);

    this.setupLighting();
    this.createStarField();
  }

  getScene(): THREE.Scene {
    return this.scene;
  }

  addEntity(id: string, object: THREE.Object3D): void {
    this.entities.set(id, object);
    this.scene.add(object);
  }

  removeEntity(id: string): void {
    const obj = this.entities.get(id);
    if (obj) {
      this.scene.remove(obj);
      this.entities.delete(id);
    }
  }

  getEntity(id: string): THREE.Object3D | undefined {
    return this.entities.get(id);
  }

  update(dt: number, cameraPosition?: THREE.Vector3): void {
    this.starField.rotation.y += dt * 0.002;
    // Re-anchor the starfield to the camera so the player is always at
    // the centre of the star sphere. Without this, the static starfield
    // lives at the world origin and is left tens of thousands of units
    // behind once the player warps into a deep sector — the camera far
    // plane (20000) clips them out and the void looks empty.
    if (cameraPosition) {
      this.starField.position.copy(cameraPosition);
    }
  }

  dispose(): void {
    this.entities.forEach((obj) => this.scene.remove(obj));
    this.entities.clear();
    this.scene.clear();
  }

  private setupLighting(): void {
    const ambient = new THREE.AmbientLight(AMBIENT_COLOR, 0.8);
    this.scene.add(ambient);

    const hemi = new THREE.HemisphereLight(0x6688cc, 0x222233, 0.5);
    this.scene.add(hemi);

    const dirLight = new THREE.DirectionalLight(DIR_LIGHT_COLOR, 1.4);
    dirLight.position.set(50, 80, 100);
    this.scene.add(dirLight);

    const rimLight = new THREE.DirectionalLight(0x4466aa, 0.6);
    rimLight.position.set(-30, -20, -60);
    this.scene.add(rimLight);
  }

  private createStarField(): void {
    const positions = new Float32Array(STAR_COUNT * 3);
    const colors = new Float32Array(STAR_COUNT * 3);
    const sizes = new Float32Array(STAR_COUNT);

    for (let i = 0; i < STAR_COUNT; i++) {
      const i3 = i * 3;

      // Spherical distribution on a thick shell so no star ever ends up
      // close to the camera (which would render as a bright pixel-blob).
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = STAR_FIELD_RADIUS_MIN + Math.random() * (STAR_FIELD_RADIUS - STAR_FIELD_RADIUS_MIN);

      positions[i3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = r * Math.cos(phi);

      // Mostly white/blue-ish, some warmer stars
      const temp = Math.random();
      if (temp < 0.6) {
        colors[i3] = 0.8 + Math.random() * 0.2;
        colors[i3 + 1] = 0.9 + Math.random() * 0.1;
        colors[i3 + 2] = 1.0;
      } else if (temp < 0.85) {
        colors[i3] = 0.5 + Math.random() * 0.3;
        colors[i3 + 1] = 1.0;
        colors[i3 + 2] = 0.5 + Math.random() * 0.3;
      } else {
        colors[i3] = 1.0;
        colors[i3 + 1] = 0.8 + Math.random() * 0.2;
        colors[i3 + 2] = 0.4 + Math.random() * 0.3;
      }

      sizes[i] = 1.0 + Math.random() * 3.0;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const material = new THREE.PointsMaterial({
      // `sizeAttenuation: true` makes Three.js compute the on-screen
      // pixel size as roughly `size * (canvasHeight/2) / distanceToCamera`.
      // Stars sit on a 4-9k radius shell, so size=30 gives an apparent
      // ~3-7 px star at 1080p — small enough to read as "stars", big
      // enough that the alpha sprite isn't lost to subpixel rounding.
      // (Original code used size=2 which collapsed to <1 px and was
      // effectively invisible regardless of star count.)
      size: 30,
      sizeAttenuation: true,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      // Stars opt out of fog so they keep their colour in deep zones
      // (where the scene fog is intentionally aggressive). The geometry
      // sits on a 4–9 k radius shell which is well past the fog far of
      // 2.6 k, so without `fog: false` they would dim to invisible.
      fog: false,
      // Alpha-mapped circular sprite. Without this Three.js renders
      // gl.POINTS as flat coloured squares — visible but ugly and easy
      // to mistake for "no stars" against the near-black background.
      map: createStarSprite(),
      alphaTest: 0.04,
    });

    this.starField = new THREE.Points(geometry, material);
    // Stars live in their own render bucket so they always paint behind
    // gameplay objects (asteroids, ships, station hulls). Without this
    // the additive blending can make a star pop in front of an entity
    // for a frame when the depth sort flips on an exact tie.
    this.starField.renderOrder = -1000;
    this.scene.add(this.starField);
  }
}

/**
 * Tiny soft radial-gradient texture used as the alpha map for every
 * star point. Generated once at scene boot and kept on the GPU for the
 * lifetime of the scene — disposed implicitly when the scene is
 * garbage-collected on game exit.
 */
function createStarSprite(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, cx);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  grd.addColorStop(0.7, 'rgba(255,255,255,0.12)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grd;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}
